// Firebase initialization — runs once at app boot.
//
// What this module does:
//   1. Reads Firebase Web config from Vite env vars (public-by-design).
//   2. Initializes App Check with reCAPTCHA Enterprise FIRST so token
//      attachment is wired into every later service. (See § "Init order"
//      below — App Check MUST be initialised before Auth/Firestore/Functions/
//      Storage, or those services won't pick up App Check tokens. Stage B v1
//      shipped with the wrong order and broke production on 2026-05-28.)
//   3. Initializes Auth, Firestore, Functions, Storage, Messaging.
//   4. If VITE_USE_EMULATORS=true, points every SDK at the local emulators.
//
// This module exports the live SDK instances. Anywhere else in the app that
// needs Firestore / Auth / Functions, import them from here.
//
// --- Init order ---
//
// The Firebase Web SDK requires `initializeAppCheck` to run BEFORE
// `getAuth` / `getFirestore` / `getFunctions` / `getStorage`. The provider
// integration that attaches App Check tokens to outgoing service requests is
// wired at service-construction time; services constructed before App Check
// silently send all requests WITHOUT tokens, and there is no runtime warning.
// Empirically (2026-05-28), having App Check after the services produced a
// fully-loaded reCAPTCHA badge and grecaptcha.enterprise object but ZERO
// requests to firebaseappcheck.googleapis.com — Firestore went out unattested
// and was rejected by `request.app != null` rules. Issue #28 has the full
// post-mortem.

import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  initializeAuth,
  connectAuthEmulator,
  GoogleAuthProvider,
  browserLocalPersistence,
  type Auth,
} from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, type Firestore } from 'firebase/firestore';
// functions / storage / messaging are intentionally NOT imported for their
// runtime values here (IMP-048): a value import pulls those SDK modules into
// the eager boot chunk even though none are needed for first paint, App Check,
// auth, or the earliest Firestore reads. We dynamically `import()` them at
// first use below (getFunctionsLazy / getStorageLazy / getMessagingIfSupported)
// so their code lands in separate async chunks. Only the *types* are imported,
// which are erased at build time and carry no runtime dependency.
import type { Functions, HttpsCallable } from 'firebase/functions';
import type { FirebaseStorage } from 'firebase/storage';
import type { Messaging } from 'firebase/messaging';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  CustomProvider,
  getToken,
  type AppCheck,
} from 'firebase/app-check';

import { resolveAuthDomain } from './authDomain';

// --- Config -----------------------------------------------------------------

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  // The page's own host on team.onlogic.com and the Firebase domains, so the
  // sign-in handler is on the same site as the app; the configured value
  // elsewhere (previews, local). See lib/authDomain.ts and GitHub issue 296.
  authDomain: resolveAuthDomain(
    typeof location === 'undefined' ? '' : location.hostname,
    import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  ),
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const useEmulators = import.meta.env.VITE_USE_EMULATORS === 'true';

// In development, missing config is annoying-but-survivable (emulators).
// In production, it's fatal — fail loudly.
if (!config.apiKey && !useEmulators) {
  // eslint-disable-next-line no-console
  console.error(
    'Firebase config is missing. Copy .env.example to .env.local and fill in values.',
  );
}

// --- Initialize Firebase app ------------------------------------------------

export const app: FirebaseApp = initializeApp(config);

// --- App Check (MUST come before Auth/Firestore/Functions/Storage) ----------

let appCheck: AppCheck | undefined;
const disableAppCheck = import.meta.env.VITE_DISABLE_APP_CHECK === 'true';

if (import.meta.env.VITE_RECAPTCHA_SITE_KEY && !disableAppCheck) {
  try {
    if (useEmulators) {
      // In emulator mode, use a CustomProvider with a dummy JWT token.
      // The Firestore emulator accepts any correctly formatted JWT as an App Check token
      // without validating its signature, which lets us bypass `request.app != null` locally
      // without attempting to contact the real Firebase App Check backend (which 403s on unregistered tokens).
      const dummyToken = 'eyJhbGciOiJub25lIiwidHlwZSI6IkpXVCJ9.eyJhcHBfaWQiOiIxOjkxMTI4MTIyMDU5NTp3ZWI6ODE3NTNiMjM3NmIzNzk0YTY4YzA4NiJ9.';
      appCheck = initializeAppCheck(app, {
        provider: new CustomProvider({
          getToken: () => Promise.resolve({
            token: dummyToken,
            expireTimeMillis: Date.now() + 86400000
          })
        }),
        isTokenAutoRefreshEnabled: false,
      });
    } else {
      // App Check debug token. Setting the FIREBASE_APPCHECK_DEBUG_TOKEN global
      // BEFORE initializeAppCheck makes the SDK exchange a debug token instead of
      // running a real reCAPTCHA Enterprise attestation. Two cases use it:
      //
      //   - Local Vite dev (import.meta.env.DEV): `true` makes the SDK generate a
      //     random debug token and log it to the console for one-off registration.
      //   - Preview-channel builds: VITE_APPCHECK_DEBUG_TOKEN carries a *registered*
      //     debug token so the app can attest on the temporary
      //     `onlogic-team--<channel>-<hash>.web.app` preview URL, which is NOT in
      //     the reCAPTCHA Enterprise domain allowlist. It is injected ONLY by the
      //     PR-preview workflow (.github/workflows/preview.yml); the production
      //     deploy never sets it, so the live site always uses real reCAPTCHA.
      //
      // SECURITY: a registered debug token is an App Check bypass for the whole
      // project and ships publicly in the preview bundle. Keep it preview-only and
      // rotate/revoke it in the Console as needed. App Check is one layer — Auth,
      // Firestore rules, and per-callable checks still apply.
      const previewDebugToken = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN;
      if (import.meta.env.DEV) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
      } else if (previewDebugToken) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = previewDebugToken;
        // eslint-disable-next-line no-console
        console.info('[firebase] App Check using a preview debug token (non-production build)');
      }
      appCheck = initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(import.meta.env.VITE_RECAPTCHA_SITE_KEY),
        isTokenAutoRefreshEnabled: true,
      });
    }
    // eslint-disable-next-line no-console
    console.info('[firebase] App Check initialised', useEmulators ? '(emulator)' : '(production)');
  } catch (err) {
    // Surface init failures loudly. A silent failure here is what bit us on
    // 2026-05-28 (issue #28) — token mint never started but the page kept
    // running, and Firestore reads were denied with permission errors.
    // eslint-disable-next-line no-console
    console.error('[firebase] App Check init failed:', err);
  }
} else if (!disableAppCheck) {
  // No site key configured. In emulator builds this is fine because we use
  // a CustomProvider; but in any production-flavoured build it's a misconfiguration.
  // eslint-disable-next-line no-console
  console.warn(
    '[firebase] App Check NOT initialised: VITE_RECAPTCHA_SITE_KEY is empty. ' +
    'No App Check token will be minted; with App Check enforcement enabled at the ' +
    'service level (Firebase Console), Firestore and callable requests from this ' +
    'client will be rejected.',
  );
}
export { appCheck };

// --- App Check token priming (non-blocking) ---------------------------------
// Diagnosed 2026-06-01 (live capture): App Check was initialised in production
// but the SDK had not yet minted a token at boot — there was no
// firebaseappcheck.googleapis.com exchange — so Firestore's startup streaming
// reads went out unattested and were rejected once App Check was enforced.
// The original fix PRIMED the token (forced an exchange) before the first read
// and made `main.tsx` await that exchange before mounting the app tree at all
// (plus, briefly, a fixed 600ms "propagation" sleep — removed in IMP-046 once
// `getToken()` was shown to populate App Check's state before resolving; the
// SDK has no separate propagation step).
//
// IMP-058 removed that app-level mount gate entirely (see `main.tsx` — it now
// renders immediately). Verified directly against the installed SDKs
// (@firebase/app-check 0.11.3, @firebase/firestore, @firebase/functions, all
// pinned via firebase@12.13.0):
//   - Firestore's App Check credential provider (`FirebaseAppCheckTokenProvider
//     .getToken()`) and the Functions callable client
//     (`ContextProvider.getAppCheckToken()`) each call the App Check instance's
//     OWN `getToken()` before every outgoing request/RPC, which awaits any
//     in-flight exchange (`state.exchangeTokenPromise`, shared across
//     concurrent callers) rather than sending a cached-only or empty token. So
//     each service already blocks its own requests on a real token — an
//     app-level gate in front of all of them was pure serialization, not an
//     additional safety property.
//   - The one real gap: each SDK wires up its OWN reference to the App Check
//     instance asynchronously, shortly after `getFirestore(app)` /
//     `getFunctions(app)` are constructed (a same-tick/next-microtask race) —
//     a request fired before that wiring completes goes out with no App Check
//     header and no wait, not a delayed one. This isn't reachable in our boot
//     sequence: App Check initialises (synchronously) before any service is
//     constructed (the init-order rule above, kept intact), and this app's
//     first real Firestore reads / callable calls happen only after the full
//     module graph evaluates, React mounts, routes lazy-load, and (for
//     callables, which are constructed lazily — see IMP-048) a user acts or
//     `requestIdleCallback` fires on the Home page — many ticks later than the
//     SDKs' own wiring needs.
//   - A stalled/failed App Check exchange can't hang a callable either: the
//     SDK catches attestation errors internally and resolves a dummy/error
//     token rather than rejecting, so the request still goes out (and gets
//     denied server-side by `enforceAppCheck`, surfaced to the caller as a
//     normal retryable error — see `lib/errors.ts`). That is a narrow,
//     recoverable failure mode, not the every-read-on-every-load breakage
//     that #28 was.
//
// Priming still matters for latency: starting the exchange at module load
// means the first *real* request usually finds a token already minted (or
// already in flight and shared via `exchangeTokenPromise`) instead of
// starting the exchange cold.
async function primeAppCheckToken(ac: AppCheck): Promise<void> {
  try {
    await getToken(ac);
    // eslint-disable-next-line no-console
    console.info('[firebase] App Check token primed');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[firebase] App Check token priming failed:', e);
  }
}

if (appCheck) {
  void primeAppCheckToken(appCheck);
}

// --- Other Firebase services (initialized AFTER App Check) -------------------
// auth + firestore are on the first-paint critical path, so they stay eager.
//
// Auth is constructed with `initializeAuth` rather than `getAuth` so that NO
// `popupRedirectResolver` is wired in at boot. Verified in @firebase/auth
// 1.13.1: `getAuth()` installs `browserPopupRedirectResolver`, and on mobile
// browsers, Safari and iOS that resolver is initialised *proactively* — during
// auth start-up, before the persisted user is even restored, the SDK loads
// Google's `gapi` script from apis.google.com and opens the hidden
// `/__/auth/iframe` from the authDomain, and waits for that iframe to answer a
// ping. Two extra script/iframe loads, serially on the sign-in restore path,
// on exactly the devices where the app felt slow to start. The resolver only
// matters for the popup sign-in itself, so `lib/auth.tsx` passes it to
// `signInWithPopup` at the one call site instead; a returning user's boot never
// touches it.
//
// Persistence stays localStorage (`browserLocalPersistence`): that is where
// every existing session already lives (the previous code migrated users there
// with `setPersistence` on each boot) and `ProtectedRoute`'s persisted-session
// heuristic looks for the `firebase:authUser:*` key there.

export const auth: Auth = initializeAuth(app, { persistence: browserLocalPersistence });

export const db: Firestore = getFirestore(app);

// --- Lazily-constructed services (IMP-048) ----------------------------------
// functions (callables) and storage (uploads) are only ever used in response to
// a user action, never at boot, so we construct them on first use behind a
// dynamic import(). This keeps the firebase/functions and firebase/storage SDK
// code out of the eager boot bundle (the largest remaining startup-JS cost
// flagged by the IMP-046 Lighthouse run). Each accessor caches its promise so
// the service — and its emulator wiring — is built exactly once.
//
// Init-order note: App Check is initialised synchronously at module load (far
// above), so it is always in place before any of these runtime accessors can
// run — the construct-App-Check-before-services rule that broke production on
// 2026-05-28 (#28) is preserved. Deferring these services only moves their
// construction *later* than App Check, never earlier.

let functionsPromise: Promise<Functions> | undefined;
/** Cloud Functions client (us-central1), constructed lazily on first call. */
export function getFunctionsLazy(): Promise<Functions> {
  if (!functionsPromise) {
    functionsPromise = (async () => {
      const { getFunctions, connectFunctionsEmulator } = await import('firebase/functions');
      // Region must match the callables in apps/functions/src/.
      const fns = getFunctions(app, 'us-central1');
      if (useEmulators) connectFunctionsEmulator(fns, '127.0.0.1', 5001);
      return fns;
    })();
  }
  return functionsPromise;
}

/**
 * A callable reference with the Functions SDK *module* loaded on first use,
 * not just the client (IMP-062). Call sites used to import `httpsCallable`
 * statically, which kept the SDK in whatever chunk imported them — through the
 * always-mounted step-up modal, that was the start-up bundle, quietly undoing
 * IMP-048. Use this instead of importing from 'firebase/functions'.
 */
export async function getCallableLazy<TIn = unknown, TOut = unknown>(
  name: string,
): Promise<HttpsCallable<TIn, TOut>> {
  const [functions, { httpsCallable }] = await Promise.all([
    getFunctionsLazy(),
    import('firebase/functions'),
  ]);
  return httpsCallable<TIn, TOut>(functions, name);
}

let storagePromise: Promise<FirebaseStorage> | undefined;
/** Cloud Storage client, constructed lazily on first call. */
export function getStorageLazy(): Promise<FirebaseStorage> {
  if (!storagePromise) {
    storagePromise = (async () => {
      const { getStorage, connectStorageEmulator } = await import('firebase/storage');
      const s = getStorage(app);
      if (useEmulators) connectStorageEmulator(s, '127.0.0.1', 9199);
      return s;
    })();
  }
  return storagePromise;
}

// --- Messaging (lazy — only on supported browsers) -------------------------
// Already runtime-lazy; now also dynamically imports the messaging SDK so it
// stays out of the boot chunk (IMP-048).

export async function getMessagingIfSupported(): Promise<Messaging | null> {
  const { getMessaging, isSupported } = await import('firebase/messaging');
  if (!(await isSupported())) return null;
  return getMessaging(app);
}

// --- Emulator wiring -------------------------------------------------------

if (useEmulators) {
  // eslint-disable-next-line no-console
  console.info('[firebase] Connecting to local emulators');
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  // Functions + Storage emulators are wired inside getFunctionsLazy /
  // getStorageLazy at construction time (IMP-048) — those services are no
  // longer built here, so their emulator hookup moved with them.
}

// --- Auth provider helpers -------------------------------------------------

export function buildGoogleProvider(): GoogleAuthProvider {
  const provider = new GoogleAuthProvider();
  // Require Google Workspace account hint — restricts the picker to onlogic.com.
  provider.setCustomParameters({
    hd: 'onlogic.com',
    prompt: 'select_account',
  });
  provider.addScope('email');
  provider.addScope('profile');
  return provider;
}
